import React from 'react';
import {
  showToast,
  SystemTenantForm,
  mountForm,
  unmountForm,
} from '@kineticdata/react';
import { generateFormButtons, FormError } from '@kineticdata/ui';
import { regRoute } from '../../router';
import { regSaga } from '../../saga';
import { call, cancel, delay, fork, put, takeEvery } from 'redux-saga/effects';
import { dispatch, regHandlers } from '../../store';
import { useParams } from 'react-router-dom';
import {
  DATABASE_ADAPTER_FIELDS,
  PostgresPropertyLayout,
} from '../common/DatabaseAdapters';
import {
  fetchTenantDeploymentStatus,
  restartTenantDeployments,
} from '@kineticdata/react/src/apis/system';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

const tenantEditIntegratorKey = spaceSlug => `${spaceSlug}-tenant-integrator`;

const handleSave = formOptions => () => {
  showToast(
    { content: `The space's integrator component was updated successfully.` },
    'success',
  );
  const formKey = tenantEditIntegratorKey(formOptions.slug);
  unmountForm(formKey);
  mountForm(formKey);
};

regRoute(
  'TENANT_EDIT_INTEGRATOR_PAGE',
  '/spaces/edit/:spaceSlug/integrator/*',
  {
    formKey: ({ payload }) => `${payload?.params?.spaceSlug}-tenant-integrator`,
  },
);

regSaga(
  takeEvery('LEAVE_TENANT_EDIT_INTEGRATOR_PAGE', function* () {
    yield cancel(integratorStatusPoller);
  }),
);

let integratorStatusPoller;
function* pollIntegratorStatus(slug) {
  while (true) {
    const { deployment } = yield call(fetchTenantDeploymentStatus, {
      slug,
      deployment: 'integrator',
    });
    yield put({
      type: 'SET_TENANT_INTEGRATOR_STATUS',
      payload: deployment?.status,
    });
    if (deployment?.status === 'Running') {
      yield cancel(integratorStatusPoller);
    } else {
      yield delay(5000);
    }
  }
}

regSaga(
  takeEvery(
    'INIT_TENANT_INTEGRATOR_STATUS',
    function* ({ payload: { slug, status } }) {
      yield put({
        type: 'SET_TENANT_INTEGRATOR_STATUS',
        payload: status,
      });
      if (!!status && !['Running', 'Not Found'].includes(status)) {
        integratorStatusPoller = yield fork(pollIntegratorStatus, slug);
      }
    },
  ),
);

regSaga(
  takeEvery('TENANT_RESTART_INTEGRATOR', function* ({ payload: slug }) {
    yield call(restartTenantDeployments, { slug, deployment: 'integrator' });
    integratorStatusPoller = yield fork(pollIntegratorStatus, slug);
  }),
);

regHandlers({
  SET_TENANT_INTEGRATOR_STATUS: (state, { payload }) =>
    state.setIn(['page', 'tenant', 'integrator', 'status'], payload),
});

const initIntegratorStatus = (slug, status) => {
  dispatch('INIT_TENANT_INTEGRATOR_STATUS', { slug, status });
};

const handleIntegratorRestart = slug => () => {
  dispatch('TENANT_RESTART_INTEGRATOR', slug);
};

const IntegratorStatusField = ({ slug }) => {
  const status = useSelector(state =>
    state.getIn(['page', 'tenant', 'integrator', 'status'], 'Loading'),
  );

  return (
    <div className="form-group">
      <label>Integrator Status</label>
      <div className="input-group input-group--map-field">
        <div
          className={classNames('form-control-plaintext rounded', {
            'animate-success-bg': status === 'Running',
            'bg-light': status === 'Loading',
            'bg-danger': status !== 'Running' && status !== 'Loading',
          })}
          style={{ maxWidth: '40vw' }}
        />
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={handleIntegratorRestart(slug)}
          disabled={status !== 'Running'}
        >
          Restart
        </button>
      </div>
      <div className="form-control-plaintext">
        <em className="fw-semibold">{status}</em>
      </div>
    </div>
  );
};

const FormLayout = ({
  fields,
  error,
  buttons,
  bindings,
  formOptions: { slug },
}) => {
  const currentAdapter = bindings.values.get('integrator_databaseAdapter_type');
  const enabled = bindings.tenant?.getIn(['features', 'integrator']) === 'true';

  return (
    <form>
      {fields.get('integrator_feature')}
      {bindings.values.get('integrator_feature') && (
        <>
          {enabled && <IntegratorStatusField slug={slug} />}
          <div className="row">
            <div className={enabled ? 'col-md-4' : 'col-md-6'}>
              {fields.get('integrator_replicas')}
            </div>
            {!enabled && (
              <div className="col-md-6">
                {fields.get('integrator_imageToggle')}
              </div>
            )}
            <div className={enabled ? 'col-md-8' : 'col-md-12'}>
              {fields.get('integrator_image')}
            </div>
          </div>
          <div className="heading heading-sm">
            <h2 className="h3">Integrator Database</h2>
          </div>
          <div className="row">
            <div className="col-6">
              {fields.get('integrator_databaseAdapter_type')}
            </div>
            <div className="col-6">
              {fields.get('integrator_autoCreateDatabase')}
            </div>
          </div>
          {currentAdapter === 'postgres' &&
          !bindings.values.get('integrator_autoCreateDatabase') ? (
            <PostgresPropertyLayout
              fields={fields}
              values={bindings.values}
              buildFieldName={name => `integrator_postgres_${name}`}
            />
          ) : null}
        </>
      )}
      {error}
      <div className="form-buttons">{buttons}</div>
    </form>
  );
};

export const SpaceEditIntegrator = ({ handleDelete }) => {
  const { spaceSlug } = useParams();
  return (
    <SystemTenantForm
      formKey={tenantEditIntegratorKey(spaceSlug)}
      onSave={handleSave}
      component="integrator"
      slug={spaceSlug}
      components={{
        FormError,
        FormButtons: generateFormButtons({
          handleDelete,
          submitLabel: 'Update Space',
          includeReset: true,
        }),
        FormLayout,
      }}
      alterFields={{
        integrator_feature: {
          renderAttributes: { switch: true },
        },
        integrator_autoCreateDatabase: {
          renderAttributes: { wrapperClassName: 'pt-v32' },
        },
        integrator_replicas: {
          renderAttributes: { min: 0 },
        },
        integrator_imageToggle: {
          renderAttributes: { wrapperClassName: 'pt-v32' },
        },
        ...DATABASE_ADAPTER_FIELDS(spaceSlug),
      }}
      onLoad={() =>
        ({ tenant }) => {
          initIntegratorStatus(
            spaceSlug,
            tenant?.getIn(['integrator', 'deployment', 'status']),
          );
        }}
    >
      {({ form, initialized }) =>
        initialized && <div className="content-container">{form}</div>
      }
    </SystemTenantForm>
  );
};
